import { Component, inject, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { AppInformationService, AuthService, BrewersUnitOfWork, SessionInformationService, UtilityService } from 'app/core/core.module';
import { Customer, CustomerType } from 'app/core/model/entity-model';
import { ReportService } from 'app/core/report.service';
import { InputDialogComponent } from 'app/shared/shared.module';
import { DxDataGridComponent } from 'devextreme-angular';
import { alert, confirm } from 'devextreme/ui/dialog';
import { BsModalService } from 'ngx-bootstrap/modal';

@Component({
    standalone: false,
    selector: 'app-customer-overview',
    templateUrl: './customer-overview.component.html',
    styleUrls: ['./customer-overview.component.scss'],
})
export class CustomerOverviewComponent implements OnInit {
    uow = inject(BrewersUnitOfWork);
    router = inject(Router);
    sessionInfo = inject(SessionInformationService);
    appInfo = inject(AppInformationService);
    auth = inject(AuthService);
    modalService = inject(BsModalService);
    utility = inject(UtilityService);
    reportService = inject(ReportService);

    filter: any = {};
    customers: Customer[];
    customerTypes: CustomerType[];
    isCustomerAdmin = false;
    salesPersons: any[];

    canEdit: boolean;

    @ViewChild('dataGrid') dataGrid: DxDataGridComponent;

    async ngOnInit() {
        this.appInfo.pleaseWaitShow();
        this.canEdit = this.auth.hasRole('Customers:Admin') || this.auth.hasRole('Admin');
        this.customerTypes = this.uow.lookups.customerTypes;
        await this.search();
        this.isCustomerAdmin = this.auth.hasRole('Customers:Admin');
        this.appInfo.pleaseWaitHide();
    }

    rowDoubleClick(e: any) {
        this.open(e.data);
    }

    async search(take?: number) {
        this.appInfo.pleaseWaitShow();
        this.customers = await this.uow.customers.search(this.filter, 'type', take);
        this.sessionInfo.setItem('customer-search-filter', this.filter);
        await this.markActiveProductPriceOverrides();
        await this.markOpenBalances();
        this.appInfo.pleaseWaitHide();
    }

    async markActiveProductPriceOverrides() {
        const customerIds = await this.uow.getCustomerIdsWithActiveProductPriceOverrides();
        this.customers.forEach((customer) => (customer.hasActiveProductPriceOverride = customerIds.has(customer.id)));
    }

    async markOpenBalances() {
        const openBalances = await this.uow.getCustomerOpenBalances();
        this.customers.forEach((customer) => (customer.openBalance = openBalances.get(customer.id) ?? 0));
    }

    clear() {
        this.filter = {};
    }

    open(customer?: any) {
        this.router.navigate([`/customers/${customer ? customer.id : 'new'}`]);
    }

    async delete(customer: any) {
        const selectedRows = this.dataGrid.instance.getSelectedRowsData();
        if (!selectedRows) return;

        if (!(await confirm('Are you sure you want to delete the selected customer?', this.appInfo.settings.company))) return;

        selectedRows.forEach((row) => {
            row.deleted = new Date();
            row.deletedBy = this.auth.currentUser.userName;
        });
        await this.uow.commit();
        await this.search();
    }

    async editType(customers: Customer[]) {
        if (!customers || customers.length === 0) return;

        const modalRef = this.modalService.show(InputDialogComponent, {
            class: 'modal-sm',
            ignoreBackdropClick: true,
            keyboard: false,
            initialState: {
                dialogHeader: 'Edit Customer Type',
                mode: 'selectWithId',
                dialogValueArray: this.customerTypes,
            },
        });

        modalRef.content?.result.subscribe(async (result) => {
            if (result && result.success && result.value) {
                this.appInfo.pleaseWaitShow();
                customers.forEach((customer) => (customer.typeId = result.value));
                await this.uow.commit();
                this.appInfo.pleaseWaitHide();
            }
            modalRef.hide();
        });
    }

    async emailStatements(customers: Customer[]) {
        if (!customers || customers.length === 0) return;

        const result = await confirm(`Are you sure you want to email the selected customer(s) their statement?`, this.appInfo.settings.company);
        if (!result) return;

        this.appInfo.pleaseWaitShow();
        const ids = customers.map((customer) => customer.id);
        const response = await this.utility.sendStatementsToQueue(ids);
        this.appInfo.pleaseWaitHide();
        await alert(response.success ? 'Statements added to queue' : `Error adding statements to queue. ${response.message}`, this.appInfo.settings.company);
    }

    async printStatement(customers: Customer[]) {
        if (!customers || customers.length === 0) return;
        this.appInfo.pleaseWaitShow();
        await this.reportService.printStatement(customers.map((customer) => customer.id));
        this.appInfo.pleaseWaitHide();
    }
}
